import { HeroIcons } from '../../lib/utils/heroIcons'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'
import useLocale from '../../hooks/useLocale'
import { getLocaleAppBaseUrl } from '../../lib/utils/language'
import BackgroundPicture from '../shared/BackgroundPicture'
import gtmService from '../../services/gtm.service'

interface StepCard {
  id: string
  number: string
  title: string
  description: string
  icon: typeof HeroIcons.MagnifyingGlassIcon
}

const cardBase: Array<Pick<StepCard, 'id' | 'number' | 'icon'>> = [
  {
    id: 'discover',
    number: '1',
    icon: HeroIcons.MagnifyingGlassIcon,
  },
  {
    id: 'invest',
    number: '2',
    icon: HeroIcons.CreditCardIcon,
  },
  {
    id: 'track',
    number: '3',
    icon: HeroIcons.ChartBarSquareIcon,
  },
]

const MIN_INVESTMENT = 10
const MAX_INVESTMENT = 10000
const INVESTMENT_STEP = 100
const FRACTION_PRICE = 10
const TARGET_YIELD = 15
const DURATION_MONTHS = 24
const PROJECT_IMAGE = '/img/ornaments/craponne-building.png'

const formatEuros = (value: number) =>
  `${Math.round(value).toLocaleString('fr-FR')} €`
const formatEurosPrecise = (value: number) =>
  `${value.toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`

export default function StepsSection() {
  const { t } = useTranslation('index')
  const { locale } = useLocale()
  const sectionRef = useRef<HTMLElement | null>(null)
  const mobileResultRef = useRef<HTMLDivElement | null>(null)
  const desktopResultRef = useRef<HTMLDivElement | null>(null)
  const resultHighlightTimeoutRef = useRef<number | null>(null)
  const hasAnimatedRef = useRef(false)
  const [isInView, setIsInView] = useState(false)
  const [investmentAmount, setInvestmentAmount] = useState(3500)
  const cardCopies = t('landing.steps.cards', {
    returnObjects: true,
  }) as Array<{ title: string; description: string }>
  const cards: StepCard[] = cardBase.map((card, index) => ({
    ...card,
    title: cardCopies[index]?.title || '',
    description: cardCopies[index]?.description || '',
  }))
  const projectName = t('landing.steps.project.name')
  const projectCity = t('landing.steps.project.city')
  const sliderValue = investmentAmount === MIN_INVESTMENT ? 0 : investmentAmount
  const updateInvestmentAmount = (value: string) => {
    const nextValue = Number(value)

    setInvestmentAmount(nextValue === 0 ? MIN_INVESTMENT : nextValue)
  }

  const sliderProgress = (sliderValue / MAX_INVESTMENT) * 100
  const fractionCount = Math.floor(investmentAmount / FRACTION_PRICE)
  const fractionsLabel = fractionCount > 1 ? 'fractions' : 'fraction'
  const yearlyPreview = (investmentAmount * TARGET_YIELD) / 100
  const totalPreview = yearlyPreview * (DURATION_MONTHS / 12)
  const formattedTargetYield = Number.isInteger(TARGET_YIELD)
    ? TARGET_YIELD.toLocaleString('fr-FR')
    : TARGET_YIELD.toLocaleString('fr-FR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })

  useEffect(() => {
    const node = sectionRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry.isIntersecting || hasAnimatedRef.current) return

        hasAnimatedRef.current = true
        setIsInView(true)
        observer.disconnect()
      },
      {
        threshold: 0.18,
        rootMargin: '0px 0px -12% 0px',
      },
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [])

  useEffect(
    () => () => {
      if (resultHighlightTimeoutRef.current) {
        window.clearTimeout(resultHighlightTimeoutRef.current)
      }
    },
    [],
  )

  const highlightResult = (node: HTMLDivElement | null) => {
    if (
      !node ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return
    }

    node.getAnimations().forEach((animation) => animation.cancel())
    node.animate(
      [
        { transform: 'translateX(0)' },
        { transform: 'translateX(-3px)' },
        { transform: 'translateX(3px)' },
        { transform: 'translateX(-2px)' },
        { transform: 'translateX(2px)' },
        { transform: 'translateX(0)' },
      ],
      {
        duration: 480,
        easing: 'ease-out',
      },
    )
  }

  const handleInvestmentCommit = () => {
    if (resultHighlightTimeoutRef.current) {
      window.clearTimeout(resultHighlightTimeoutRef.current)
    }

    const isDesktop = window.matchMedia('(min-width: 1280px)').matches
    if (isDesktop) {
      highlightResult(desktopResultRef.current)
      return
    }

    const isMobile = window.matchMedia('(max-width: 767px)').matches
    if (!isMobile) {
      highlightResult(mobileResultRef.current)
      return
    }

    mobileResultRef.current?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
      block: 'center',
    })

    resultHighlightTimeoutRef.current = window.setTimeout(() => {
      highlightResult(mobileResultRef.current)
    }, 450)
  }

  const handleInvestmentKeyUp = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (
      ['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].includes(
        event.key,
      )
    ) {
      handleInvestmentCommit()
    }
  }

  return (
    <section ref={sectionRef}>
      <div className="mx-auto w-full">
        <div className="relative overflow-hidden bg-[#2f2aa8] pb-10 pt-10 shadow-[0_26px_50px_rgba(40,33,132,0.34)] transition-all duration-700 tablet:pt-12">
          <BackgroundPicture
            fallbackSrc="/img/backgrounds/bg-dark-desktop.png"
            sources={[
              {
                srcSet: '/img/backgrounds/bg-dark-desktop.webp',
                type: 'image/webp',
              },
            ]}
          />
          <div className="pointer-events-none absolute -left-28 -top-24 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(165,181,255,0.26)_0%,transparent_72%)]" />
          <div className="pointer-events-none absolute -right-36 -top-14 h-80 w-80 rounded-full bg-[radial-gradient(circle,rgba(160,175,255,0.2)_0%,transparent_74%)]" />
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.08)_0%,rgba(255,255,255,0)_35%)]" />

          <div className="relative z-10 mx-auto w-full px-3 md:px-10 lg:px-16 xl:px-24 2xl:px-44">
            <div className="mx-auto max-w-4xl text-center text-white">
              <h2 className="mt-5 font-semibold text-white text-shelters-section-title">
                {t('landing.steps.title.line1')}
                <br />
                {t('landing.steps.title.line2')}
                <span className="text-[#9d90ff]">
                  {t('landing.steps.title.highlight')}
                </span>
              </h2>

              <p className="text-white/82 mx-auto mt-5 max-w-2xl text-shelters-section-body">
                {t('landing.steps.intro')}
              </p>
            </div>

            <div className="mt-10 hidden desktop:grid desktop:grid-cols-3 desktop:gap-5">
              {cards.map((card, index) => {
                const animationDelay = 120 + index * 90

                return (
                  <div
                    key={card.id}
                    className={`relative flex justify-center transition-all duration-700 ${
                      isInView
                        ? 'translate-y-0 opacity-100'
                        : 'translate-y-3 opacity-0'
                    }`}
                    style={{ transitionDelay: `${animationDelay}ms` }}
                  >
                    {index < cards.length - 1 && (
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none absolute left-[calc(50%+1.5rem)] top-1/2 z-0 h-px w-[calc(100%+1.25rem-3rem)] -translate-y-1/2 bg-[linear-gradient(90deg,rgba(255,255,255,0.18)_0%,rgba(171,158,255,0.78)_55%,rgba(255,255,255,0.18)_100%)] transition-opacity duration-700 ${
                          isInView ? 'opacity-100' : 'opacity-0'
                        }`}
                        style={{ transitionDelay: `${animationDelay + 60}ms` }}
                      />
                    )}

                    <span
                      className={`bg-white/14 relative z-10 inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/45 text-[26px] font-medium leading-none text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.45),0_12px_24px_rgba(18,15,68,0.24)] backdrop-blur-md transition-transform duration-700 ${
                        isInView ? 'scale-100' : 'scale-90'
                      }`}
                      style={{ transitionDelay: `${animationDelay}ms` }}
                    >
                      {card.number}
                    </span>
                  </div>
                )
              })}
            </div>

            <div className="mt-10 grid grid-cols-1 gap-5 desktop:grid-cols-3">
              {cards.map((card, index) => {
                const Icon = card.icon
                return (
                  <article
                    key={card.id}
                    className={`flex flex-col rounded-card border border-[#d8ddff] bg-[linear-gradient(160deg,#ffffff_0%,#f5f6ff_100%)] p-4 shadow-[0_18px_30px_rgba(31,27,99,0.18)] transition-all duration-700 tablet:p-5 ${
                      isInView
                        ? 'translate-y-0 opacity-100'
                        : 'translate-y-4 opacity-0'
                    }`}
                    style={{ transitionDelay: `${120 + index * 90}ms` }}
                  >
                    <div className="flex items-start gap-3">
                      <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-inner-card bg-gradient text-white shadow-[0_10px_24px_rgba(51,40,154,0.24)] desktop:h-14 desktop:w-14">
                        <Icon className="h-6 w-6 desktop:h-7 desktop:w-7" />
                      </span>
                      <div>
                        <h3 className="text-shelters-lead font-medium leading-[1.2] text-sheltersDarkBlue">
                          {card.title}
                        </h3>
                        <p className="mt-2 text-shelters-body text-sheltersDarkBlue/80">
                          {card.description}
                        </p>
                      </div>
                    </div>

                    {card.number === '1' && (
                      <>
                        <div className="mt-4 overflow-hidden rounded-inner-card border border-[#d8deff] bg-white p-3 desktop:hidden">
                          <div className="grid grid-cols-[0.95fr_1fr] gap-3">
                            <div className="relative overflow-hidden rounded-inner-card">
                              <img
                                src={PROJECT_IMAGE}
                                alt={projectName}
                                className="aspect-[1.08] h-full w-full object-cover object-[22%_52%]"
                                draggable={false}
                              />
                              <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full border border-white/80 bg-white/90 px-2 py-1 font-medium text-[#4f5ab0] shadow-[0_6px_14px_rgba(38,32,120,0.12)] text-shelters-step-card-meta">
                                <span className="h-1.5 w-1.5 rounded-full bg-[#4bc47f]" />
                                {t('landing.steps.project.open')}
                              </span>
                            </div>

                            <div className="min-w-0 pt-0.5">
                              <div className="flex items-start gap-1.5">
                                <p className="min-w-0 font-medium text-sheltersDarkBlue text-shelters-step-card-heading">
                                  {projectName}
                                </p>
                                <button
                                  type="button"
                                  className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[#dbe2ff] bg-[#f8f9ff] text-sheltersBlue transition hover:border-[#c9d2ff] hover:bg-[#f2f4ff]"
                                  aria-label={t(
                                    'landing.steps.project.viewOpportunityAria',
                                  )}
                                >
                                  <HeroIcons.ArrowRightIcon className="h-3 w-3 -rotate-45" />
                                </button>
                              </div>

                              <p className="mt-1 inline-flex max-w-full items-center gap-1 text-[#6974a9] text-shelters-step-card-meta">
                                <HeroIcons.MapPinIcon className="h-3.5 w-3.5 shrink-0" />
                                <span className="truncate">{projectCity}</span>
                              </p>

                              <div className="mt-3">
                                <div className="flex items-center justify-between gap-3 text-shelters-step-card-meta">
                                  <p className="text-[#6974a9]">
                                    {t('landing.steps.labels.funding')}
                                  </p>
                                  <p className="font-medium text-sheltersDarkBlue">
                                    64%
                                  </p>
                                </div>
                                <div className="mt-2 h-1.5 rounded-full bg-[#e3e7ff]">
                                  <div className="h-full w-[64%] rounded-full bg-[linear-gradient(90deg,#8d6dca_0%,#5d5be6_100%)]" />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="mt-3 border-t border-[#e1e6ff] pt-3">
                            <div className="grid grid-cols-3 gap-2">
                              <div className="min-w-0">
                                <p className="inline-flex items-center gap-1 text-[#6974a9] text-shelters-step-card-meta">
                                  <HeroIcons.ClockIcon className="h-3.5 w-3.5 shrink-0" />
                                  {t('landing.steps.labels.duration')}
                                </p>
                                <p className="mt-1 font-medium text-sheltersDarkBlue text-shelters-step-card-value">
                                  {t('landing.steps.labels.durationValue')}
                                </p>
                              </div>
                              <div className="min-w-0 text-center">
                                <p className="inline-flex items-center justify-center gap-1 text-[#6974a9] text-shelters-step-card-meta">
                                  <HeroIcons.BanknotesIcon className="h-3.5 w-3.5 shrink-0" />
                                  {t('landing.steps.labels.ticket')}
                                </p>
                                <p className="mt-1 font-medium text-sheltersDarkBlue text-shelters-step-card-value">
                                  10 €
                                </p>
                              </div>
                              <div className="min-w-0 text-right">
                                <p className="inline-flex items-center justify-end gap-1 text-[#6974a9] text-shelters-step-card-meta">
                                  <HeroIcons.ChartBarSquareIcon className="h-3.5 w-3.5 shrink-0" />
                                  {t('landing.steps.labels.targetYieldShort')}
                                </p>
                                <p className="mt-1 font-medium text-sheltersDarkBlue text-shelters-step-card-value">
                                  {t(
                                    'landing.steps.labels.targetYieldSlashYear',
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-5 hidden overflow-hidden rounded-inner-card border border-[#d8deff] bg-white desktop:block">
                          <div className="relative h-40 overflow-hidden">
                            <img
                              src={PROJECT_IMAGE}
                              alt={projectName}
                              className="h-full w-full object-cover object-[22%_52%]"
                              draggable={false}
                            />
                            <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full border border-white/80 bg-white/90 px-2.5 py-1 text-[12px] font-medium text-[#4f5ab0]">
                              <span className="h-2 w-2 rounded-full bg-[#4bc47f]" />
                              {t('landing.steps.project.open')}
                            </span>
                          </div>

                          <div className="p-3">
                            <div className="flex items-end justify-between gap-3">
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="text-shelters-lead font-medium leading-[1.2] text-sheltersDarkBlue">
                                    {projectName}
                                  </p>
                                  <button
                                    type="button"
                                    className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-[#dbe2ff] bg-[#f8f9ff] text-sheltersBlue transition hover:border-[#c9d2ff] hover:bg-[#f2f4ff]"
                                    aria-label={t(
                                      'landing.steps.project.viewOpportunityAria',
                                    )}
                                  >
                                    <HeroIcons.ArrowRightIcon className="h-3 w-3 -rotate-45" />
                                  </button>
                                </div>
                                <p className="mt-1 inline-flex items-center gap-1 text-shelters-kicker text-[#6974a9]">
                                  <HeroIcons.MapPinIcon className="h-3.5 w-3.5" />
                                  {projectCity}
                                </p>
                              </div>
                            </div>

                            <div className="mt-4">
                              <div className="flex items-center justify-between text-shelters-kicker">
                                <p className="text-[#6974a9]">
                                  {t('landing.steps.labels.funding')}
                                </p>
                                <p className="font-medium text-sheltersDarkBlue">
                                  64%
                                </p>
                              </div>
                              <div className="mt-2 h-2 rounded-full bg-[#e3e7ff]">
                                <div className="h-full w-[64%] rounded-full bg-[linear-gradient(90deg,#8d6dca_0%,#5d5be6_100%)]" />
                              </div>
                            </div>

                            <div className="mt-4 border-t border-[#e1e6ff] pt-3">
                              <div className="grid grid-cols-3 gap-3">
                                <div>
                                  <p className="inline-flex items-center gap-1.5 text-shelters-kicker text-[#6974a9]">
                                    <HeroIcons.ClockIcon className="h-4 w-4" />
                                    {t('landing.steps.labels.duration')}
                                  </p>
                                  <p className="mt-1 text-shelters-body font-medium text-sheltersDarkBlue">
                                    {t('landing.steps.labels.durationValue')}
                                  </p>
                                </div>
                                <div>
                                  <p className="inline-flex items-center gap-1.5 text-shelters-kicker text-[#6974a9]">
                                    <HeroIcons.BanknotesIcon className="h-4 w-4" />
                                    {t('landing.steps.labels.ticketMin')}
                                  </p>
                                  <p className="mt-1 text-shelters-body font-medium text-sheltersDarkBlue">
                                    10 €
                                  </p>
                                </div>
                                <div>
                                  <p className="inline-flex items-center gap-1.5 text-shelters-kicker text-[#6974a9]">
                                    <HeroIcons.ChartBarSquareIcon className="h-4 w-4" />
                                    {t('landing.steps.labels.targetYield')}
                                  </p>
                                  <p className="mt-1 text-shelters-body font-medium text-sheltersDarkBlue">
                                    {t(
                                      'landing.steps.labels.targetYieldPerYear',
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {card.number === '2' && (
                      <>
                        <div className="mt-4 overflow-hidden rounded-inner-card border border-[#d8deff] bg-white p-3 desktop:hidden">
                          <div className="rounded-inner-card border border-[#dfe4ff] bg-[linear-gradient(135deg,#ffffff_0%,#fbfcff_55%,#f3f5ff_100%)] px-3 py-3">
                            <p className="font-medium text-[#6974a9] text-shelters-step-card-meta">
                              {t('landing.steps.labels.amount')}
                            </p>
                            <p className="mt-1 font-semibold text-sheltersBlue text-shelters-step-card-amount">
                              {formatEuros(investmentAmount)}
                            </p>

                            <label
                              htmlFor="investment-range-mobile"
                              className="sr-only"
                            >
                              {t('landing.steps.labels.amount')}
                            </label>
                            <input
                              id="investment-range-mobile"
                              type="range"
                              min={0}
                              max={MAX_INVESTMENT}
                              step={INVESTMENT_STEP}
                              value={sliderValue}
                              onChange={(event) =>
                                updateInvestmentAmount(event.target.value)
                              }
                              onPointerUp={handleInvestmentCommit}
                              onKeyUp={handleInvestmentKeyUp}
                              className="mt-3 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-[#e4e8ff] [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-[#5d5be6] [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#5d5be6] [&::-webkit-slider-thumb]:shadow-[0_0_0_3px_rgba(165,181,255,0.28)]"
                              style={{
                                background: `linear-gradient(90deg,#5d5be6 0%,#8d6dca ${sliderProgress}%,#e4e8ff ${sliderProgress}%,#e4e8ff 100%)`,
                              }}
                            />
                            <div className="mt-2 flex items-center justify-between text-[#6f78aa] text-shelters-step-card-meta">
                              <span>{formatEuros(MIN_INVESTMENT)}</span>
                              <span>{formatEuros(MAX_INVESTMENT)}</span>
                            </div>
                          </div>

                          <div className="mt-3 rounded-inner-card border border-[#dee3ff] bg-[#f8f9ff] px-3 py-3">
                            <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-2 text-shelters-step-card-value">
                              <p className="text-[#6f78aa]">
                                {t('landing.steps.labels.project')}
                              </p>
                              <p className="max-w-[150px] truncate text-right font-medium text-sheltersDarkBlue">
                                {projectName}
                              </p>
                              <p className="text-[#6f78aa]">
                                {t('landing.steps.labels.targetYield')}
                              </p>
                              <p className="text-right font-medium text-sheltersDarkBlue">
                                {formattedTargetYield}{' '}
                                {t('landing.steps.labels.perYear')}
                              </p>
                              <p className="text-[#6f78aa]">
                                {t('landing.steps.labels.duration')}
                              </p>
                              <p className="text-right font-medium text-sheltersDarkBlue">
                                {t('landing.steps.labels.durationValue')}
                              </p>
                            </div>
                          </div>

                          <div className="mt-3 rounded-inner-card border border-[#e3e6f7] bg-[linear-gradient(90deg,#f7f8ff_0%,#ffffff_100%)] px-4 py-3">
                            <p className="inline-flex w-full items-center justify-center gap-2 font-semibold text-[#4f5ab0] text-shelters-step-card-value">
                              <HeroIcons.LockClosedIcon className="h-4 w-4 text-[#6b76ca]" />
                              {t('landing.steps.labels.securePayment')}
                            </p>
                          </div>
                        </div>

                        <div className="mt-5 hidden rounded-inner-card border border-[#d8deff] bg-white p-3 desktop:block">
                          <div className="bg-[#fbfcff] p-3">
                            <p className="text-shelters-kicker text-[#6974a9]">
                              {t('landing.steps.labels.amount')}
                            </p>
                            <p className="mt-2 text-shelters-lead font-medium leading-[1.2] text-sheltersBlue">
                              {formatEuros(investmentAmount)}
                            </p>

                            <label
                              htmlFor="investment-range-desktop"
                              className="sr-only"
                            >
                              {t('landing.steps.labels.amount')}
                            </label>
                            <input
                              id="investment-range-desktop"
                              type="range"
                              min={0}
                              max={MAX_INVESTMENT}
                              step={INVESTMENT_STEP}
                              value={sliderValue}
                              onChange={(event) =>
                                updateInvestmentAmount(event.target.value)
                              }
                              onPointerUp={handleInvestmentCommit}
                              onKeyUp={handleInvestmentKeyUp}
                              className="mt-3 h-2 w-full cursor-pointer appearance-none rounded-full bg-[#e4e8ff]"
                              style={{
                                background: `linear-gradient(90deg,#5d5be6 0%,#8d6dca ${sliderProgress}%,#e4e8ff ${sliderProgress}%,#e4e8ff 100%)`,
                              }}
                            />
                            <div className="mt-2 flex items-center justify-between text-shelters-kicker text-[#6f78aa]">
                              <span>{formatEuros(MIN_INVESTMENT)}</span>
                              <span>{formatEuros(MAX_INVESTMENT)}</span>
                            </div>
                          </div>

                          <div className="mt-4 rounded-inner-card border border-[#dee3ff] bg-[#f8f9ff] p-3">
                            <p className="text-shelters-body font-medium text-sheltersDarkBlue">
                              {t('landing.steps.labels.summary')}
                            </p>
                            <div className="mt-3 grid grid-cols-2 gap-y-2 text-shelters-kicker">
                              <p className="text-[#6f78aa]">
                                {t('landing.steps.labels.project')}
                              </p>
                              <p className="text-right font-medium text-sheltersDarkBlue">
                                {projectName}
                              </p>
                              <p className="text-[#6f78aa]">
                                {t('landing.steps.labels.targetYield')}
                              </p>
                              <p className="text-right font-medium text-sheltersDarkBlue">
                                {formattedTargetYield}{' '}
                                {t('landing.steps.labels.perYear')}
                              </p>
                              <p className="text-[#6f78aa]">
                                {t('landing.steps.labels.duration')}
                              </p>
                              <p className="text-right font-medium text-sheltersDarkBlue">
                                {t('landing.steps.labels.durationValue')}
                              </p>
                            </div>
                          </div>

                          <div className="mt-4 rounded-inner-card border border-[#e3e6f7] bg-[#f2f3fb] px-4 py-3">
                            <p className="inline-flex w-full items-center justify-center gap-2 text-shelters-body font-medium text-[#4f5ab0]">
                              <HeroIcons.LockClosedIcon className="h-4 w-4 text-[#6b76ca]" />
                              {t('landing.steps.labels.securePayment')}
                            </p>
                          </div>
                        </div>
                      </>
                    )}

                    {card.number === '3' && (
                      <>
                        <div className="mt-4 overflow-hidden rounded-inner-card border border-[#d8deff] bg-white p-3 desktop:hidden">
                          <p className="font-semibold text-sheltersDarkBlue text-shelters-step-card-heading">
                            {t('landing.steps.labels.portfolio')}
                          </p>

                          <div
                            ref={mobileResultRef}
                            className="mt-3 scroll-mt-28 rounded-inner-card border border-[#dee3ff] bg-[linear-gradient(135deg,#ffffff_0%,#f7f9ff_52%,#f1f4ff_100%)] px-3 py-3"
                          >
                            <div className="grid grid-cols-2 gap-3">
                              <div className="min-w-0">
                                <p className="text-[#6f78aa] text-shelters-step-card-meta">
                                  {t('landing.steps.labels.totalReturn')}
                                </p>
                                <p className="mt-1 font-semibold text-[#2d9a68] text-shelters-step-card-amount">
                                  +{formatEurosPrecise(totalPreview)}
                                </p>
                              </div>
                              <div className="min-w-0 text-right">
                                <p className="text-[#6f78aa] text-shelters-step-card-meta">
                                  {t('landing.steps.labels.returnRate')}
                                </p>
                                <p className="mt-1 font-semibold text-sheltersBlue text-shelters-step-card-amount">
                                  {formattedTargetYield}%
                                  <span className="ml-1 text-shelters-step-card-meta">
                                    {t('landing.steps.labels.perYear')}
                                  </span>
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="mt-4">
                            <p className="font-semibold text-sheltersDarkBlue text-shelters-step-card-heading">
                              {t('landing.steps.labels.investments')}
                            </p>

                            <div className="mt-2 overflow-hidden rounded-inner-card border border-[#dee3ff] bg-[#f8f9ff]">
                              <div className="flex items-center justify-between gap-3 px-3 py-3">
                                <div className="flex min-w-0 items-center gap-3">
                                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded-inner-card border border-[#d3dbff]">
                                    <img
                                      src={PROJECT_IMAGE}
                                      alt={t(
                                        'landing.steps.project.thumbnailAlt',
                                      )}
                                      className="h-full w-full object-cover object-[30%_50%]"
                                      draggable={false}
                                    />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="truncate font-medium text-sheltersDarkBlue text-shelters-step-card-heading">
                                      {projectName}
                                    </p>
                                    <p className="mt-0.5 text-[#6f78aa] text-shelters-step-card-value">
                                      {fractionCount.toLocaleString('fr-FR')}{' '}
                                      {fractionsLabel}
                                    </p>
                                  </div>
                                </div>

                                <p className="shrink-0 self-center text-right font-semibold text-sheltersDarkBlue text-shelters-step-card-amount">
                                  {formatEuros(investmentAmount)}
                                </p>
                              </div>

                              <a
                                href={getLocaleAppBaseUrl(locale, 'register')}
                                onClick={() =>
                                  gtmService.ctaClicked({
                                    ctaId: 'steps_mobile_view_investments',
                                    ctaLabel: t(
                                      'landing.steps.labels.viewAllInvestments',
                                    ),
                                    ctaLocation: 'steps_section',
                                    destinationUrl: getLocaleAppBaseUrl(
                                      locale,
                                      'register',
                                    ),
                                    destinationType: 'app_register',
                                    language: locale,
                                  })
                                }
                                className="group flex w-full items-center justify-center gap-2 border-t border-[#e2e7ff] bg-white/70 px-3 py-3 text-center font-medium text-sheltersBlue transition text-shelters-step-card-value hover:bg-white"
                              >
                                {t('landing.steps.labels.viewAllInvestments')}
                                <HeroIcons.ArrowRightIcon className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                              </a>
                            </div>
                          </div>
                        </div>

                        <div className="mt-5 hidden grow flex-col rounded-inner-card border border-[#d8deff] bg-white p-3 desktop:flex">
                          <div className="grow">
                            <p className="text-shelters-lead font-medium text-sheltersDarkBlue">
                              {t('landing.steps.labels.portfolio')}
                            </p>
                            <p className="mt-0.5 text-shelters-kicker text-[#6f78aa]">
                              {t('landing.steps.labels.overview')}
                            </p>

                            <div
                              ref={desktopResultRef}
                              className="mt-3 rounded-inner-card border border-[#dee3ff] bg-[linear-gradient(135deg,#fbfcff_0%,#f1f4ff_100%)] px-3 py-5"
                            >
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <p className="text-shelters-kicker text-[#6f78aa]">
                                    {t('landing.steps.labels.totalReturn')}
                                  </p>
                                  <p className="mt-1 text-shelters-lead font-medium text-[#2d9a68]">
                                    +{formatEurosPrecise(totalPreview)}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-shelters-kicker text-[#6f78aa]">
                                    {t('landing.steps.labels.returnRate')}
                                  </p>
                                  <p className="mt-1 text-shelters-lead font-medium text-sheltersBlue">
                                    {formattedTargetYield}%
                                    <span className="ml-1 text-shelters-kicker">
                                      {t('landing.steps.labels.perYear')}
                                    </span>
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="pt-3">
                            <p className="text-shelters-body font-medium text-sheltersDarkBlue">
                              {t('landing.steps.labels.investments')}
                            </p>

                            <div className="mt-2 overflow-hidden rounded-inner-card border border-[#dee3ff] bg-[#f8f9ff]">
                              <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                                <div className="flex min-w-0 items-center gap-3">
                                  <div className="h-11 w-11 shrink-0 overflow-hidden rounded-inner-card border border-[#d3dbff]">
                                    <img
                                      src={PROJECT_IMAGE}
                                      alt={t(
                                        'landing.steps.project.thumbnailAlt',
                                      )}
                                      className="h-full w-full object-cover object-[30%_50%]"
                                      draggable={false}
                                    />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="truncate text-shelters-body font-medium text-sheltersDarkBlue">
                                      {projectName}
                                    </p>
                                    <p className="mt-0.5 text-shelters-kicker text-[#6f78aa]">
                                      {fractionCount.toLocaleString('fr-FR')}{' '}
                                      {fractionsLabel}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex shrink-0 self-end">
                                  <p className="text-right text-shelters-kicker font-medium text-sheltersDarkBlue">
                                    {formatEuros(investmentAmount)}
                                  </p>
                                </div>
                              </div>

                              <a
                                href={getLocaleAppBaseUrl(locale, 'register')}
                                onClick={() =>
                                  gtmService.ctaClicked({
                                    ctaId: 'steps_desktop_view_investments',
                                    ctaLabel: t(
                                      'landing.steps.labels.viewAllInvestments',
                                    ),
                                    ctaLocation: 'steps_section',
                                    destinationUrl: getLocaleAppBaseUrl(
                                      locale,
                                      'register',
                                    ),
                                    destinationType: 'app_register',
                                    language: locale,
                                  })
                                }
                                className="block w-full border-t border-[#e2e7ff] bg-white/55 px-3 py-3 text-center text-shelters-body text-sheltersBlue"
                              >
                                {t('landing.steps.labels.viewAllInvestments')}
                              </a>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </article>
                )
              })}
            </div>

            <div className="mx-auto mt-9 flex w-full max-w-[860px] flex-col items-center">
              <a
                href={getLocaleAppBaseUrl(locale, 'register')}
                onClick={() =>
                  gtmService.ctaClicked({
                    ctaId: 'steps_main_register',
                    ctaLabel: t('landing.steps.cta'),
                    ctaLocation: 'steps_section',
                    destinationUrl: getLocaleAppBaseUrl(locale, 'register'),
                    destinationType: 'app_register',
                    language: locale,
                  })
                }
                className="group relative mx-auto inline-flex h-16 w-full max-w-[560px] items-center justify-center rounded-button border border-[#d9dcf8] bg-[#f8f9ff] px-8 text-sheltersBlue shadow-[0_14px_28px_rgba(23,18,86,0.24)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_32px_rgba(23,18,86,0.3)]"
              >
                <span className="font-medium leading-none text-shelters-hero-action">
                  {t('landing.steps.cta')}
                </span>
                <HeroIcons.ArrowRightIcon className="absolute right-8 h-7 w-7 transition-transform duration-300 group-hover:translate-x-0.5" />
              </a>

              <div className="mt-5 grid w-full max-w-[340px] grid-cols-2 items-center gap-x-5 gap-y-3 text-white tablet:flex tablet:max-w-none tablet:flex-wrap tablet:justify-center tablet:gap-x-7 tablet:gap-y-2">
                <p className="inline-flex items-center justify-center gap-2 font-medium drop-shadow-[0_1px_2px_rgba(16,14,64,0.38)] text-shelters-step-card-meta tablet:text-shelters-body">
                  <HeroIcons.ChartBarSquareIcon className="h-4 w-4 text-[#ced8ff] tablet:h-[18px] tablet:w-[18px]" />
                  {t('landing.steps.assurances.realTimeTracking')}
                </p>
                <p className="inline-flex items-center justify-center gap-2 font-medium drop-shadow-[0_1px_2px_rgba(16,14,64,0.38)] text-shelters-step-card-meta tablet:text-shelters-body">
                  <HeroIcons.LockClosedIcon className="h-4 w-4 text-[#ced8ff] tablet:h-[18px] tablet:w-[18px]" />
                  {t('landing.steps.assurances.securePayment')}
                </p>
                <p className="col-span-2 inline-flex items-center justify-center gap-2 font-medium drop-shadow-[0_1px_2px_rgba(16,14,64,0.38)] text-shelters-step-card-meta tablet:col-span-1 tablet:text-shelters-body">
                  <HeroIcons.BanknotesIcon className="h-4 w-4 text-[#ced8ff] tablet:h-[18px] tablet:w-[18px]" />
                  {t('landing.steps.assurances.fromTen')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
